// ─────────────────────────────────────────────────────────────────────────────
// authService.js — Phase 2
// Trainer authentication — register, login, logout, session restore.
//
// Students are NOT Supabase Auth users. They authenticate via personal codes
// validated through an Edge Function (implemented in P2-S3).
//
// All functions are async and throw on error — callers must try/catch.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

export const authService = {

  // ── Register a new trainer account ────────────────────────────────────────
  // fullName and organisation are stored in user_metadata (visible to the user,
  // not used for RLS). Email + password are managed by Supabase Auth.
  async register({ email, password, fullName, organisation }) {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          organisation: organisation.trim(),
          role: 'TRAINER',
        },
      },
    });
    if (error) throw error;
    return data;
  },

  // ── Login existing trainer ─────────────────────────────────────────────────
  async login({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) throw error;
    return data;
  },

  // ── Logout ─────────────────────────────────────────────────────────────────
  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // ── Restore session from storage (called on app open) ─────────────────────
  // Returns the existing Supabase session if one is stored (persistSession=true).
  // Returns null if no session exists (user must log in or continue as guest).
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  // ── Get current user (requires active session) ────────────────────────────
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  // ── Send password reset email ─────────────────────────────────────────────
  // Supabase sends a reset link to the email. No admin involvement needed.
  // The link redirects back to the app where the user sets a new password.
  async resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: window.location.origin }
    );
    if (error) throw error;
  },

  // ── Listen for auth state changes ─────────────────────────────────────────
  // Returns an unsubscribe function. Call it in useEffect cleanup.
  onAuthStateChange(callback) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => callback(event, session)
    );
    return () => subscription.unsubscribe();
  },

  // ── Extract display name from user object ─────────────────────────────────
  getDisplayName(user) {
    if (!user) return 'Trainer';
    return user.user_metadata?.full_name || user.email || 'Trainer';
  },

  // ── P2-S6: 24h reset delay — check or create a pending reset row ─────────
  // Returns { status: 'queued'|'pending'|'ready', id, hours_remaining }
  // 'queued'  — first request, row created, come back in 24h
  // 'pending' — row exists but not yet due
  // 'ready'   — 24h has elapsed, caller must fire authService.resetPassword()
  async checkOrCreatePendingReset(email) {
    const { data, error } = await supabase.rpc('check_or_create_pending_reset', {
      p_email: email.trim().toLowerCase(),
    });
    if (error) throw error;
    return data;
  },

  // ── P2-S6: Cancel a pending reset (user clicked "cancel") ────────────────
  async cancelPendingReset(id, email) {
    const { error } = await supabase.rpc('cancel_pending_reset', {
      p_id:    id,
      p_email: email.trim().toLowerCase(),
    });
    if (error) throw error;
  },

  // ── P2-S6: Update trainer display name ───────────────────────────────────
  async updateProfile({ fullName }) {
    const { data, error } = await supabase.auth.updateUser({
      data: { full_name: fullName.trim() },
    });
    if (error) throw error;
    return data.user;
  },

  // ── P2-S6: Change password for the currently logged-in trainer ───────────
  // Requires an active session — call only when authenticated.
  async updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },
};
